"use client";

import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  IconButton,
  Box,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  FaHandshake,
  FaUserGraduate,
  FaUsers,
  FaLightbulb,
  FaBook,
  FaClipboardList,
  FaQuestionCircle,
  FaStar,
  FaStickyNote,
  FaCheckCircle,
  FaTimes,
} from "react-icons/fa";

interface InterviewGuidelinesModalProps {
  open: boolean;
  onClose: () => void;
}

export const InterviewGuidelinesModal: React.FC<
  InterviewGuidelinesModalProps
> = ({ open, onClose }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "10px",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: "#1976d2",
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <FaClipboardList />
          <Typography variant="h5" component="span" fontWeight="bold">
            Interviewer Guidelines
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: "white" }}>
          <FaTimes />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 3 }}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              bgcolor: "#f8f9fa",
              borderLeft: "4px solid #1976d2",
            }}
          >
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Purpose of the Interview
            </Typography>
            <Typography variant="body1">
              The interview aims to assess the student’s academic achievements,
              leadership potential, extracurricular involvement, financial need,
              and alignment with the objectives of the Holding Hand trust.
            </Typography>
          </Paper>

          <Typography
            variant="h6"
            gutterBottom
            fontWeight="bold"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              color: "#1976d2",
            }}
          >
            <FaBook />
            Pre-Interview Preparation
          </Typography>
          <List>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon sx={{ minWidth: "36px" }}>
                <FaCheckCircle color="#1976d2" />
              </ListItemIcon>
              <ListItemText
                primary="Review Student's Application"
                secondary="Examine the student's academic records, personal statement, recommendation letters, and any submitted documentation on financial need."
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon sx={{ minWidth: "36px" }}>
                <FaCheckCircle color="#1976d2" />
              </ListItemIcon>
              <ListItemText
                primary="Understand Scholarship Criteria"
                secondary="Familiarize yourself with the Holding Hands objectives, criterion and the specific qualities desired in scholarship recipients (e.g., leadership, community service, academic excellence)."
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon sx={{ minWidth: "36px" }}>
                <FaCheckCircle color="#1976d2" />
              </ListItemIcon>
              <ListItemText
                primary="Prepare Questions"
                secondary="Draft open-ended questions related to academic goals, personal challenges, and motivations for applying for the scholarship."
              />
            </ListItem>
          </List>

          <Divider sx={{ my: 3 }} />

          <Typography
            variant="h6"
            gutterBottom
            fontWeight="bold"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              color: "#1976d2",
            }}
          >
            <FaQuestionCircle />
            Interview Process
          </Typography>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              1. Introduction:
            </Typography>
            <List dense>
              <ListItem sx={{ pl: 2 }}>
                <ListItemIcon sx={{ minWidth: "24px" }}>
                  <FaStar color="#1976d2" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Greet the student warmly and introduce yourself and the panel if it's a panel interview." />
              </ListItem>
              <ListItem sx={{ pl: 2 }}>
                <ListItemIcon sx={{ minWidth: "24px" }}>
                  <FaStar color="#1976d2" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Introduce aims and objectives of Holding Hands Trust." />
              </ListItem>
              <ListItem sx={{ pl: 2 }}>
                <ListItemIcon sx={{ minWidth: "24px" }}>
                  <FaStar color="#1976d2" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Briefly explain the purpose of the interview and outline the structure." />
              </ListItem>
            </List>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              2. Core Question Areas:
            </Typography>

            <Box sx={{ pl: 2, mb: 2 }}>
              <Typography
                variant="body2"
                fontWeight="bold"
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <FaBook color="#1976d2" fontSize="small" />
                Academic Excellence:
              </Typography>
              <List dense>
                <ListItem sx={{ pl: 2 }}>
                  <ListItemText
                    primary={
                      '"What subjects or areas of study are you most passionate about, and why?"'
                    }
                  />
                </ListItem>
                <ListItem sx={{ pl: 2 }}>
                  <ListItemText
                    primary={
                      '"Can you describe a significant academic challenge and how you overcame it?"'
                    }
                  />
                </ListItem>
              </List>
            </Box>

            <Box sx={{ pl: 2, mb: 2 }}>
              <Typography
                variant="body2"
                fontWeight="bold"
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <FaUsers color="#1976d2" fontSize="small" />
                Leadership & Extracurricular Activities:
              </Typography>
              <List dense>
                <ListItem sx={{ pl: 2 }}>
                  <ListItemText
                    primary={
                      '"Have you taken on any leadership roles? How did they impact your growth?"'
                    }
                  />
                </ListItem>
                <ListItem sx={{ pl: 2 }}>
                  <ListItemText
                    primary={
                      '"What extracurricular activities are you most proud of, and why?"'
                    }
                  />
                </ListItem>
              </List>
            </Box>

            <Box sx={{ pl: 2, mb: 2 }}>
              <Typography
                variant="body2"
                fontWeight="bold"
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <FaHandshake color="#1976d2" fontSize="small" />
                Financial Need & Personal Motivation:
              </Typography>
              <List dense>
                <ListItem sx={{ pl: 2 }}>
                  <ListItemText
                    primary={
                      '"How will this scholarship impact your education and future goals?"'
                    }
                  />
                </ListItem>
                <ListItem sx={{ pl: 2 }}>
                  <ListItemText
                    primary={
                      '"What inspired you to apply for this scholarship?"'
                    }
                  />
                </ListItem>
              </List>
            </Box>

            <Box sx={{ pl: 2, mb: 2 }}>
              <Typography
                variant="body2"
                fontWeight="bold"
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <FaLightbulb color="#1976d2" fontSize="small" />
                Alignment with Scholarship Values:
              </Typography>
              <List dense>
                <ListItem sx={{ pl: 2 }}>
                  <ListItemText
                    primary={
                      '"How do you plan to contribute to your community during or after your studies?"'
                    }
                  />
                </ListItem>
              </List>
            </Box>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              3. Take Notes:
            </Typography>
            <List dense>
              <ListItem sx={{ pl: 2 }}>
                <ListItemIcon sx={{ minWidth: "24px" }}>
                  <FaStickyNote color="#1976d2" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Record specific examples and insights shared by the student." />
              </ListItem>
            </List>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              4. Wrap-Up:
            </Typography>
            <List dense>
              <ListItem sx={{ pl: 2 }}>
                <ListItemIcon sx={{ minWidth: "24px" }}>
                  <FaStar color="#1976d2" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Allow the student to ask questions about the scholarship or trust." />
              </ListItem>
              <ListItem sx={{ pl: 2 }}>
                <ListItemIcon sx={{ minWidth: "24px" }}>
                  <FaStar color="#1976d2" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Conclude by explaining the next steps in the selection process." />
              </ListItem>
            </List>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography
            variant="h6"
            gutterBottom
            fontWeight="bold"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              color: "#1976d2",
            }}
          >
            <FaUserGraduate color="#1976d2" />
            Evaluation Criteria
          </Typography>
          <List>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon sx={{ minWidth: "36px" }}>
                <FaCheckCircle color="#4caf50" />
              </ListItemIcon>
              <ListItemText
                primary="Good"
                secondary="Student demonstrates exceptional academic potential, leadership skills, clear financial need, and strong alignment with the scholarship's objectives."
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon sx={{ minWidth: "36px" }}>
                <FaCheckCircle color="#ff9800" />
              </ListItemIcon>
              <ListItemText
                primary="Average"
                secondary="Student meets basic requirements but lacks distinction in one or more areas."
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon sx={{ minWidth: "36px" }}>
                <FaCheckCircle color="#f44336" />
              </ListItemIcon>
              <ListItemText
                primary="Below Average"
                secondary="Student does not meet the key scholarship criteria or fails to articulate goals or alignment with Holding Hand's values."
              />
            </ListItem>
          </List>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
